import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';
import { AuthService } from '../../services/authService';

const CounselorManagementModal = ({ isOpen, onClose, currentUser, onCounselorsUpdated, initialClientId = '' }) => {
    const [counselors, setCounselors] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Form states
    const [showAddForm, setShowAddForm] = useState(false);
    const [newCounselor, setNewCounselor] = useState({
        name: '',
        email: '',
        uid: '',
        role: 'counselor',
        canOrganizational: true,
        testPassword: ''
    });

    // Edit state
    const [editingCounselor, setEditingCounselor] = useState(null);
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        role: 'counselor',
        canOrganizational: true,
        testPassword: ''
    });

    // Reassignment state
    const [selectedClientToReassign, setSelectedClientToReassign] = useState(initialClientId || '');
    const [targetCounselorId, setTargetCounselorId] = useState('');
    const [reassigning, setReassigning] = useState(false);

    // Password reset sending state
    const [sendingResetFor, setSendingResetFor] = useState(null);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [counselorsWithStats, allClients] = await Promise.all([
                DataManager.getAllCounselorsWithStats(),
                DataManager.getClients()
            ]);
            setCounselors(counselorsWithStats);
            setClients(allClients);
        } catch (err) {
            console.error("Error loading counselor management data:", err);
            setError("Impossibile caricare i dati dei counselor.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadData();
            if (initialClientId) {
                setSelectedClientToReassign(initialClientId);
            }
        }
    }, [isOpen, initialClientId]);

    const handleCreateCounselor = async (e) => {
        e.preventDefault();
        const trimmedEmail = newCounselor.email.trim().toLowerCase();
        const trimmedName = newCounselor.name.trim();

        if (!trimmedName || !trimmedEmail) {
            setError("Nome ed Email sono obbligatori.");
            return;
        }

        // Prevenzione duplicazione counselor
        if (counselors.some(c => (c.email || '').toLowerCase().trim() === trimmedEmail)) {
            setError(`⚠️ Esiste già un profilo counselor associato all'email: ${trimmedEmail}. Non è consentito creare duplicati.`);
            return;
        }

        if (newCounselor.testPassword && newCounselor.testPassword.length < 6) {
            setError("La password di prova deve contenere almeno 6 caratteri.");
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const passwordToSet = newCounselor.testPassword?.trim() || 'Awareness2026!';
            let assignedUid = newCounselor.uid.trim();
            const extraData = {
                initialPassword: passwordToSet,
                canOrganizational: newCounselor.canOrganizational ?? true,
                specializations: (newCounselor.canOrganizational ?? true) ? ['individuale', 'organizzativo', 'people_care'] : ['individuale']
            };

            // Automatically provision account in Firebase Auth
            try {
                const authRes = await AuthService.createUserWithPassword(trimmedEmail, passwordToSet);
                if (authRes.created && authRes.uid) {
                    assignedUid = authRes.uid;
                } else if (authRes.reason === 'EMAIL_EXISTS') {
                    console.info("Firebase Auth account already exists for this email, linking profile.");
                }
            } catch (authErr) {
                console.warn("Auth creation notice:", authErr.message);
            }

            if (!assignedUid) {
                assignedUid = `counselor_${Date.now()}`;
            }

            await DataManager.createCounselor(
                assignedUid,
                trimmedEmail,
                trimmedName,
                newCounselor.role,
                extraData
            );

            setSuccessMessage(`✅ Counselor "${trimmedName}" creato in Firestore e registrato in Firebase Auth!\n🏢 Abilitato a Counseling Organizzativo (BuenaVista)\n🔑 Credenziali di accesso:\nEmail: ${trimmedEmail}\nPassword: ${passwordToSet}`);
            setNewCounselor({ name: '', email: '', uid: '', role: 'counselor', canOrganizational: true, testPassword: '' });
            setShowAddForm(false);
            await loadData();
            if (onCounselorsUpdated) onCounselorsUpdated();
        } catch (err) {
            console.error("Error creating counselor:", err);
            setError("Errore durante la creazione del counselor: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleStartEdit = (counselor) => {
        setEditingCounselor(counselor);
        setEditForm({
            name: counselor.name || '',
            email: counselor.email || '',
            role: counselor.role || 'counselor',
            canOrganizational: counselor.canOrganizational !== false,
            testPassword: counselor.initialPassword || ''
        });
        setShowAddForm(false);
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        if (!editingCounselor) return;

        const trimmedEmail = editForm.email.trim().toLowerCase();
        const trimmedName = editForm.name.trim();

        if (!trimmedName || !trimmedEmail) {
            setError("Nome ed Email sono obbligatori.");
            return;
        }

        if (editForm.testPassword && editForm.testPassword.length < 6) {
            setError("La password di prova deve contenere almeno 6 caratteri.");
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const canOrg = editForm.canOrganizational !== false;
            const updateData = {
                name: trimmedName,
                email: trimmedEmail,
                role: editForm.role,
                canOrganizational: canOrg,
                specializations: canOrg ? ['individuale', 'organizzativo', 'people_care'] : ['individuale']
            };

            if (editForm.testPassword) {
                updateData.initialPassword = editForm.testPassword;
                // Attempt to create auth user if they don't exist yet
                try {
                    await AuthService.createUserWithPassword(trimmedEmail, editForm.testPassword);
                } catch (authErr) {
                    console.info("Auth provision notice:", authErr.message);
                }
            }

            await DataManager.updateCounselor(editingCounselor.id, updateData);

            setSuccessMessage(`✅ Profilo counselor "${trimmedName}" aggiornato con successo!`);
            setEditingCounselor(null);
            await loadData();
            if (onCounselorsUpdated) onCounselorsUpdated();
        } catch (err) {
            console.error("Error updating counselor:", err);
            setError("Errore durante l'aggiornamento del counselor: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCounselor = async (counselor) => {
        // Prevent deleting current user
        if (currentUser && (counselor.id === currentUser.uid || counselor.email?.toLowerCase() === currentUser.email?.toLowerCase())) {
            alert("⚠️ Non puoi eliminare il profilo attualmente connesso.");
            return;
        }

        // Prevent deleting if clients assigned
        if (counselor.clientCount > 0) {
            alert(`⚠️ Impossibile eliminare: "${counselor.name}" ha attualmente ${counselor.clientCount} client${counselor.clientCount === 1 ? 'e' : 'i'} assegnat${counselor.clientCount === 1 ? 'o' : 'i'}.\n\nUtilizza la sezione "Riassegnazione Rapida Clienti" qui sotto per trasferire i clienti prima di procedere.`);
            return;
        }

        const confirmDelete = window.confirm(`Sei sicuro di voler eliminare definitivamente il profilo counselor di "${counselor.name}" (${counselor.email})?\n\nL'operazione è irreversibile.`);
        if (!confirmDelete) return;

        try {
            setLoading(true);
            setError(null);
            await DataManager.deleteCounselor(counselor.id);
            setSuccessMessage(`🗑️ Counselor "${counselor.name}" rimosso con successo.`);
            if (editingCounselor?.id === counselor.id) {
                setEditingCounselor(null);
            }
            await loadData();
            if (onCounselorsUpdated) onCounselorsUpdated();
        } catch (err) {
            console.error("Error deleting counselor:", err);
            setError("Errore durante l'eliminazione del counselor: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSendPasswordReset = async (email) => {
        if (!email) return;
        const cleanEmail = email.trim().toLowerCase();
        try {
            setSendingResetFor(email);
            const res = await AuthService.ensureUserInAuthAndReset(cleanEmail);
            let msg = `📧 Email di reimpostazione password inviata a:\n${cleanEmail}`;
            if (res.createdInAuth) {
                msg += `\n\n🔑 Nota: L'account non era ancora presente in Firebase Auth ed è stato registrato adesso.\nPassword iniziale: ${res.tempPassword}`;
            } else {
                msg += `\n\n💡 Se non la trovi, controlla la cartella SPAM / Posta Indesiderata.`;
            }
            alert(msg);
        } catch (err) {
            console.error("Error sending reset password:", err);
            alert(`Errore nell'invio del reset password: ${err.message}`);
        } finally {
            setSendingResetFor(null);
        }
    };

    const handleReassignClient = async () => {
        if (!selectedClientToReassign || !targetCounselorId) {
            alert("Seleziona sia il cliente che il nuovo counselor di destinazione.");
            return;
        }

        try {
            setReassigning(true);
            const targetCounselor = counselors.find(c => c.id === targetCounselorId);
            await DataManager.reassignClientCounselor(
                selectedClientToReassign,
                targetCounselorId,
                targetCounselor?.email || '',
                targetCounselor?.name || ''
            );

            setSuccessMessage("✅ Cliente riassegnato con successo!");
            setSelectedClientToReassign('');
            setTargetCounselorId('');
            await loadData();
            if (onCounselorsUpdated) onCounselorsUpdated();
        } catch (err) {
            console.error("Error reassigning client:", err);
            alert("Errore riassegnazione: " + err.message);
        } finally {
            setReassigning(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
                
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
                    <div className="flex items-center gap-3">
                        <span className="text-3xl">👥</span>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Gestione Organico Counselor</h2>
                            <p className="text-xs text-gray-500">Amministrazione accessi, credenziali, ruoli e bilanciamento carichi clienti</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl font-bold transition"
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    
                    {/* Alerts */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex justify-between items-center">
                            <span>⚠️ {error}</span>
                            <button onClick={() => setError(null)} className="text-xs font-bold underline ml-2">Chiudi</button>
                        </div>
                    )}
                    {successMessage && (
                        <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-2xl text-sm flex justify-between items-center">
                            <span>{successMessage}</span>
                            <button onClick={() => setSuccessMessage('')} className="text-xs underline font-bold ml-2">Chiudi</button>
                        </div>
                    )}

                    {/* Caseload Metrics */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-slate-50 border border-slate-200/70 p-4 rounded-2xl">
                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Totale Counselor</p>
                            <p className="text-2xl font-bold text-slate-800 mt-1">{counselors.length}</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200/70 p-4 rounded-2xl">
                            <p className="text-[10px] font-black uppercase text-blue-500 tracking-wider">Clienti Totali</p>
                            <p className="text-2xl font-bold text-blue-900 mt-1">{clients.length}</p>
                        </div>
                        <div className="bg-indigo-50 border border-indigo-200/70 p-4 rounded-2xl">
                            <p className="text-[10px] font-black uppercase text-indigo-500 tracking-wider">Media Clienti / Counselor</p>
                            <p className="text-2xl font-bold text-indigo-900 mt-1">
                                {counselors.length > 0 ? (clients.length / counselors.length).toFixed(1) : 0}
                            </p>
                        </div>
                    </div>

                    {/* Counselor List Section */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="text-base font-bold text-gray-800">Elenco Professionisti</h3>
                            <button
                                onClick={() => {
                                    setShowAddForm(!showAddForm);
                                    setEditingCounselor(null);
                                }}
                                className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-bold hover:bg-slate-900 transition flex items-center gap-1.5"
                            >
                                <span>{showAddForm ? '✕ Annulla' : '➕ Aggiungi Counselor'}</span>
                            </button>
                        </div>

                        {/* Add Counselor Form */}
                        {showAddForm && (
                            <form onSubmit={handleCreateCounselor} className="bg-slate-50 border border-slate-200 p-5 rounded-2xl space-y-4 animate-fade-in shadow-sm">
                                <div className="flex justify-between items-center border-b border-slate-200/60 pb-3">
                                    <h4 className="text-sm font-bold text-slate-800">➕ Registra Nuovo Counselor</h4>
                                    <span className="text-[11px] text-slate-500">* Campi obbligatori</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1">Nome e Cognome *</label>
                                        <input
                                            type="text"
                                            required
                                            value={newCounselor.name}
                                            onChange={(e) => setNewCounselor({ ...newCounselor, name: e.target.value })}
                                            placeholder="Es. Dott.ssa Anna Rossi"
                                            className="w-full p-2.5 text-sm border rounded-xl bg-white outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1">Email di Accesso *</label>
                                        <input
                                            type="email"
                                            required
                                            value={newCounselor.email}
                                            onChange={(e) => setNewCounselor({ ...newCounselor, email: e.target.value })}
                                            placeholder="anna.rossi@awarenesstool.it"
                                            className="w-full p-2.5 text-sm border rounded-xl bg-white outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1">Ruolo</label>
                                        <select
                                            value={newCounselor.role}
                                            onChange={(e) => setNewCounselor({ ...newCounselor, role: e.target.value })}
                                            className="w-full p-2.5 text-sm border rounded-xl bg-white outline-none focus:border-blue-500"
                                        >
                                            <option value="counselor">Counselor Standard</option>
                                            <option value="admin">Super Admin / Supervisore</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-600 mb-1">
                                            Password di Prova <span className="text-slate-400 font-normal">(Opzionale, min. 6 car.)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={newCounselor.testPassword}
                                            onChange={(e) => setNewCounselor({ ...newCounselor, testPassword: e.target.value })}
                                            placeholder="Es. Benvenuto2026!"
                                            className="w-full p-2.5 text-sm border rounded-xl bg-white outline-none focus:border-blue-500 font-mono text-xs"
                                        />
                                        <p className="text-[10px] text-slate-500 mt-1">
                                            🔑 Se inserita, crea subito l'account con email e password per l'accesso diretto.
                                        </p>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="flex items-center gap-2 p-3 bg-indigo-50/70 border border-indigo-200 rounded-xl cursor-pointer hover:bg-indigo-50 transition">
                                            <input
                                                type="checkbox"
                                                checked={newCounselor.canOrganizational !== false}
                                                onChange={(e) => setNewCounselor({ ...newCounselor, canOrganizational: e.target.checked })}
                                                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                            />
                                            <div>
                                                <span className="text-xs font-bold text-indigo-950 block">🏢 Abilita al Counseling Organizzativo / BuenaVista</span>
                                                <span className="text-[10px] text-indigo-700">Consente la gestione di clienti con setting aziendale, questionari People Care e anagrafica enti.</span>
                                            </div>
                                        </label>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs font-bold text-slate-600 mb-1">
                                            Firebase Auth UID <span className="text-slate-400 font-normal">(Opzionale)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={newCounselor.uid}
                                            onChange={(e) => setNewCounselor({ ...newCounselor, uid: e.target.value })}
                                            placeholder="Auto-associato al primo login se vuoto"
                                            className="w-full p-2.5 text-sm border rounded-xl bg-white outline-none focus:border-blue-500 font-mono text-xs"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddForm(false)}
                                        className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl"
                                    >
                                        Annulla
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-5 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition"
                                    >
                                        {loading ? 'Salvataggio...' : 'Salva Counselor'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Edit Counselor Form */}
                        {editingCounselor && (
                            <form onSubmit={handleSaveEdit} className="bg-amber-50/60 border border-amber-200 p-5 rounded-2xl space-y-4 animate-fade-in shadow-sm">
                                <div className="flex justify-between items-center border-b border-amber-200 pb-3">
                                    <div>
                                        <h4 className="text-sm font-bold text-amber-900">✏️ Modifica Counselor: {editingCounselor.name}</h4>
                                        <p className="text-[11px] text-amber-700">ID: {editingCounselor.id}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setEditingCounselor(null)}
                                        className="text-amber-800 hover:text-amber-950 text-xs font-bold"
                                    >
                                        ✕ Annulla Modifica
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Nome e Cognome *</label>
                                        <input
                                            type="text"
                                            required
                                            value={editForm.name}
                                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                            className="w-full p-2.5 text-sm border border-amber-300 rounded-xl bg-white outline-none focus:border-amber-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Email di Accesso *</label>
                                        <input
                                            type="email"
                                            required
                                            value={editForm.email}
                                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                            className="w-full p-2.5 text-sm border border-amber-300 rounded-xl bg-white outline-none focus:border-amber-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Ruolo</label>
                                        <select
                                            value={editForm.role}
                                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                                            className="w-full p-2.5 text-sm border border-amber-300 rounded-xl bg-white outline-none focus:border-amber-500"
                                        >
                                            <option value="counselor">Counselor Standard</option>
                                            <option value="admin">Super Admin / Supervisore</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Password di Prova / Aggiorna <span className="text-slate-400 font-normal">(Opzionale)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={editForm.testPassword}
                                            onChange={(e) => setEditForm({ ...editForm, testPassword: e.target.value })}
                                            placeholder="Nuova password di prova..."
                                            className="w-full p-2.5 text-sm border border-amber-300 rounded-xl bg-white outline-none focus:border-amber-500 font-mono text-xs"
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label className="flex items-center gap-2 p-3 bg-amber-100/60 border border-amber-300 rounded-xl cursor-pointer hover:bg-amber-100 transition">
                                            <input
                                                type="checkbox"
                                                checked={editForm.canOrganizational !== false}
                                                onChange={(e) => setEditForm({ ...editForm, canOrganizational: e.target.checked })}
                                                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                                            />
                                            <div>
                                                <span className="text-xs font-bold text-amber-950 block">🏢 Abilita al Counseling Organizzativo / BuenaVista</span>
                                                <span className="text-[10px] text-amber-800">Consente la gestione di clienti con setting aziendale, questionari People Care e anagrafica enti.</span>
                                            </div>
                                        </label>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-2 border-t border-amber-200/70">
                                    <button
                                        type="button"
                                        onClick={() => handleSendPasswordReset(editForm.email)}
                                        disabled={sendingResetFor === editForm.email}
                                        className="text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-3 py-2 rounded-xl transition flex items-center gap-1.5"
                                    >
                                        <span>📧 Invia email di reset password</span>
                                    </button>

                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setEditingCounselor(null)}
                                            className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl"
                                        >
                                            Annulla
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-5 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700 transition"
                                        >
                                            {loading ? 'Salvataggio...' : 'Salva Modifiche'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}

                        {/* Counselor Table / Cards */}
                        {loading && counselors.length === 0 ? (
                            <div className="p-8 text-center text-gray-400">Caricamento counselor in corso...</div>
                        ) : counselors.length === 0 ? (
                            <div className="p-8 text-center bg-gray-50 rounded-2xl text-gray-500">Nessun counselor registrato.</div>
                        ) : (
                            <div className="divide-y divide-gray-100 border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
                                {counselors.map((c) => (
                                    <div key={c.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-gray-50/70 transition">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                                                {c.name ? c.name[0].toUpperCase() : 'C'}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-bold text-gray-800">{c.name || 'Senza Nome'}</p>
                                                    {c.role === 'admin' ? (
                                                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-black rounded-full uppercase tracking-wider">
                                                            Admin
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full uppercase tracking-wider">
                                                            Counselor
                                                        </span>
                                                    )}
                                                    {c.canOrganizational !== false && (
                                                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 border border-indigo-200 text-[10px] font-bold rounded-full flex items-center gap-1">
                                                            <span>🏢</span> Organizzativo
                                                        </span>
                                                    )}
                                                    {currentUser && (c.id === currentUser.uid || c.email?.toLowerCase() === currentUser.email?.toLowerCase()) && (
                                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full">
                                                            Tu
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500">{c.email}</p>
                                                {(() => {
                                                    const isGoogleAccount = c.authMethod === 'google' || c.email?.toLowerCase().endsWith('@gmail.com');
                                                    if (isGoogleAccount) {
                                                        return (
                                                            <p className="text-[11px] text-sky-700 font-semibold mt-0.5 flex items-center gap-1">
                                                                <span>🌐 Accesso con Google (senza password)</span>
                                                            </p>
                                                        );
                                                    }
                                                    if (c.initialPassword) {
                                                        return (
                                                            <p className="text-[11px] text-amber-700 font-mono mt-0.5 flex items-center gap-1">
                                                                <span>🔑 Pwd prova:</span>
                                                                <span className="bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">{c.initialPassword}</span>
                                                            </p>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3 self-end md:self-center flex-wrap">
                                            <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-bold">
                                                {c.clientCount || 0} clienti
                                            </span>

                                            {/* Reset password button (only for non-Google email/password accounts) */}
                                            {!c.email?.toLowerCase().endsWith('@gmail.com') && (
                                                <button
                                                    onClick={() => handleSendPasswordReset(c.email)}
                                                    disabled={sendingResetFor === c.email}
                                                    title="Invia email di reset password"
                                                    className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition flex items-center gap-1"
                                                >
                                                    <span>📧</span>
                                                    <span className="hidden sm:inline">Reset Pwd</span>
                                                </button>
                                            )}

                                            {/* Edit Button */}
                                            <button
                                                onClick={() => handleStartEdit(c)}
                                                title="Modifica counselor"
                                                className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 rounded-lg text-xs font-bold transition flex items-center gap-1"
                                            >
                                                <span>✏️</span>
                                                <span>Modifica</span>
                                            </button>

                                            {/* Delete Button */}
                                            <button
                                                onClick={() => handleDeleteCounselor(c)}
                                                title="Elimina counselor"
                                                className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-bold transition flex items-center gap-1"
                                            >
                                                <span>🗑️</span>
                                                <span>Elimina</span>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Client Reassignment Tool */}
                    <div className="bg-indigo-50/40 border border-indigo-100 rounded-2xl p-5 space-y-4">
                        <div className="flex items-center gap-2">
                            <span className="text-xl">🔄</span>
                            <div>
                                <h3 className="text-sm font-bold text-indigo-950">Riassegnazione Rapida Clienti</h3>
                                <p className="text-xs text-indigo-700">Trasferisci la presa in carico di un cliente da un counselor all'altro</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold text-indigo-900 mb-1">Seleziona Cliente</label>
                                <select
                                    value={selectedClientToReassign}
                                    onChange={(e) => setSelectedClientToReassign(e.target.value)}
                                    className="w-full p-2.5 text-xs border border-indigo-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option value="">-- Seleziona Cliente --</option>
                                    {clients.map(cl => {
                                        const assignedTo = counselors.find(c => 
                                            c.id === cl.counselorId || 
                                            (c.email && cl.counselorEmail && c.email.toLowerCase() === cl.counselorEmail.toLowerCase()) ||
                                            (c.email && cl.counselorId && c.email.toLowerCase() === cl.counselorId.toLowerCase())
                                        );
                                        return (
                                            <option key={cl.id} value={cl.id}>
                                                {cl.name || cl.email || 'Cliente'} ({assignedTo ? assignedTo.name : (cl.counselorName || 'Non Assegnato')})
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-indigo-900 mb-1">Nuovo Counselor di Riferimento</label>
                                <select
                                    value={targetCounselorId}
                                    onChange={(e) => setTargetCounselorId(e.target.value)}
                                    className="w-full p-2.5 text-xs border border-indigo-200 rounded-xl bg-white outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option value="">-- Assegna a... --</option>
                                    {counselors.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.name} ({c.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={handleReassignClient}
                                disabled={reassigning || !selectedClientToReassign || !targetCounselorId}
                                className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 transition shadow-sm"
                            >
                                {reassigning ? 'Riassegnazione...' : 'Conferma Trasferimento'}
                            </button>
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gray-800 text-white rounded-xl text-sm font-bold hover:bg-gray-700 transition"
                    >
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CounselorManagementModal;
